// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import * as girosApi from '../api'

//Tiempo de vida del catálogo de giros en el store (30 minutos)
export const GIROS_CACHE_TTL = 30 * 60 * 1000

const asList = (data) => (Array.isArray(data) ? data : [])

const isLatest = (state, action) => state.requestId === action.meta.requestId

export const getGiros = createAsyncThunk('appGiros/getGiros', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await girosApi.fetchGiros(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const giros = getState()?.giros

        if (!giros) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //que pudo salir antes de la escritura; su respuesta se descarta por requestId
        if (giros.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (giros.status === 'pending') return false
        //Store vacío
        if (!giros.data?.length) return true

        return Date.now() - (giros.lastFetch ?? 0) > GIROS_CACHE_TTL
    }
})

export const appGirosSlice = createSlice({
    name: 'appGiros',
    initialState: {
        data: [],
        isRequested: true,
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null
    },
    reducers: {
        enableRequest: (state) => {
            state.isRequested = true
        }
    },
    extraReducers: builder => {
        builder.addCase(getGiros.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getGiros.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = asList(action.payload.data)
            state.lastFetch = action.payload.fetchedAt
        })
        builder.addCase(getGiros.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = action.payload ?? action.error?.message ?? null
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })
    }
})

export const { enableRequest } = appGirosSlice.actions

//La recarga no se espera: el alta/edición se resuelve en cuanto responde el backend y
//la lista nueva llega después con `status: 'pending'` mientras tanto
export const addGiro = createAsyncThunk('appGiros/addGiro', async (giro, { dispatch, rejectWithValue }) => {
    try {
        const data = await girosApi.createGiro(giro)
        dispatch(enableRequest())
        dispatch(getGiros())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateGiro = createAsyncThunk('appGiros/updateGiro', async (payload, { dispatch, rejectWithValue }) => {
    try {
        const data = await girosApi.updateGiro(payload.id, payload.giro)
        dispatch(enableRequest())
        dispatch(getGiros())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export default appGirosSlice.reducer
